<template>
	<div class="aruba-detail">
		<div class="aruba-body">
			<v-alert outlined text dense type="info" class="text-body-2 mb-4">
				Le API di Aruba Fatturazione Elettronica sono disponibili solo per le utenze <b>Premium</b> (o Base collegate a una Premium con delega). Usare le stesse credenziali dell'accesso a
				fatturazioneelettronica.aruba.it.
			</v-alert>

			<div class="aruba-section">Credenziali</div>
			<div class="aruba-grid">
				<v-text-field v-model="setting.username" label="Utente" autocomplete="off" dense outlined hide-details background-color="white" />
				<v-text-field
					v-model="setting.password"
					:type="mostraPassword ? 'text' : 'password'"
					:append-icon="mostraPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
					:label="setting.hasPassword ? 'Password (salvata: lasciare vuoto per non cambiarla)' : 'Password'"
					autocomplete="new-password"
					dense
					outlined
					hide-details
					background-color="white"
					@click:append="mostraPassword = !mostraPassword"
				/>
				<v-select v-model="setting.ambiente" :items="ambienti" label="Ambiente" dense outlined hide-details background-color="white" />
			</div>

			<div class="aruba-section mt-5">Dati fiscali per le fatture</div>
			<div class="aruba-grid">
				<v-select v-model="setting.regimeFiscale" :items="opzioni.regimi" item-text="text" item-value="code" label="Regime fiscale" dense outlined hide-details background-color="white" />
				<v-select v-model="setting.ivaTrasporto" :items="aliquote" label="IVA spese di spedizione (%)" dense outlined hide-details background-color="white" />
				<v-select
					v-model="setting.natura0"
					:items="opzioni.nature"
					item-text="text"
					item-value="code"
					label="Natura per le righe con IVA 0%"
					clearable
					dense
					outlined
					hide-details
					background-color="white"
				/>
				<v-text-field v-model="setting.riferimentoNormativo" label="Riferimento normativo (IVA 0%)" dense outlined hide-details background-color="white" />
				<v-text-field
					v-model="setting.sezionale"
					label="Sezionale (facoltativo)"
					hint="Es. WEB: le fatture saranno 1/WEB, 2/WEB… e non si mescolano con quelle fatte a mano"
					persistent-hint
					dense
					outlined
					background-color="white"
				/>
			</div>

			<div class="aruba-section mt-5">I tuoi dati (cedente)</div>
			<div class="text-caption grey--text text--darken-1 mb-2">Lasciare vuoto per usare i dati aziendali del profilo.</div>
			<div class="aruba-grid">
				<v-text-field v-model="cedente.denominazione" label="Ragione sociale" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.piva" label="Partita IVA" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.cf" label="Codice fiscale" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.indirizzo" label="Indirizzo" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.cap" label="CAP" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.comune" label="Comune" dense outlined hide-details background-color="white" />
				<v-text-field v-model="cedente.provincia" label="Provincia (sigla)" maxlength="2" dense outlined hide-details background-color="white" />
			</div>

			<div class="aruba-section mt-5">Pagamenti (modalità FatturaPA)</div>
			<div class="aruba-grid">
				<v-select
					v-for="p in metodiPagamento"
					:key="p"
					:value="codicePagamento(p)"
					:items="[{ code: '', text: 'Automatico' }, ...opzioni.modalita]"
					item-text="text"
					item-value="code"
					:label="p"
					dense
					outlined
					hide-details
					background-color="white"
					@change="impostaPagamento(p, $event)"
				/>
			</div>

			<template v-if="test.esito">
				<v-alert v-if="test.esito.success" outlined text dense type="success" class="text-body-2 mt-4 mb-0">
					<div class="font-weight-medium">Connessione riuscita</div>
					<div v-for="riga in datiUtenza" :key="riga.label" class="aruba-kv">
						<span>{{ riga.label }}</span
						><span>{{ riga.value }}</span>
					</div>
				</v-alert>
				<v-alert v-else outlined text dense type="error" class="text-body-2 mt-4 mb-0">{{ test.esito.message }}</v-alert>
			</template>
		</div>

		<div class="aruba-foot">
			<v-btn small outlined color="primary" :loading="test.loading" @click="$emit('test')"><v-icon left small>mdi-lan-connect</v-icon>Testa connessione</v-btn>
			<v-spacer></v-spacer>
			<v-btn small depressed color="primary" @click="$emit('save')">Salva</v-btn>
		</div>
	</div>
</template>

<script>
// riquadro di configurazione di Aruba Fatturazione Elettronica: riga espansa (desktop) e card (mobile).
// I campi (credenziali e dati fiscali delle fatture) scrivono in modello.setting; salvataggio e prova
// di connessione li fa la pagina.
// La password salvata non arriva mai qui: `hasPassword` dice solo se esiste.
import _ from 'lodash';
import { OPZIONI_FATTURA } from './arubaOpzioni';

export default {
	name: 'ArubaPanel',
	props: {
		modello: { type: Object, required: true },
		// { loading, esito: { success, data | message } }
		test: { type: Object, default: () => ({ loading: false, esito: null }) },
	},
	data() {
		return {
			opzioni: OPZIONI_FATTURA,
			metodiPagamento: [],
			mostraPassword: false,
			ambienti: [
				{ text: 'Produzione', value: 'produzione' },
				{ text: 'Demo (test)', value: 'demo' },
			],
		};
	},
	created() {
		// campi fiscali dentro modello.setting: si salvano con Salva insieme alle credenziali
		if (!this.setting.cedente) this.$set(this.setting, 'cedente', {});
		if (!Array.isArray(this.setting.pagamenti)) this.$set(this.setting, 'pagamenti', []);
		if (!this.setting.regimeFiscale) this.$set(this.setting, 'regimeFiscale', 'RF01');
		if (this.setting.ivaTrasporto === undefined || this.setting.ivaTrasporto === null || this.setting.ivaTrasporto === '') this.$set(this.setting, 'ivaTrasporto', 22);
		['natura0', 'riferimentoNormativo', 'sezionale'].forEach(k => {
			if (this.setting[k] === undefined) this.$set(this.setting, k, '');
		});
		this.$store
			.dispatch('paymentmethods/fetchLists')
			.then(res => {
				this.metodiPagamento = _.uniq(((res && res.data) || []).map(p => p.value).filter(Boolean));
			})
			.catch(() => {});
	},
	computed: {
		setting() {
			return this.modello.setting || {};
		},
		cedente() {
			return this.setting.cedente || {};
		},
		aliquote() {
			const profilo = ((this.$store.getters['settings/getSetting'] || {}).vat || []).filter(v => v.is_active).map(v => v.vat);
			return _.uniq([...profilo, 22, 10, 4, 0]).sort((a, b) => b - a);
		},
		datiUtenza() {
			const d = (this.test.esito && this.test.esito.data) || {};
			const scadenza = d.expirationDate ? this.$moment(d.expirationDate).format('DD/MM/YYYY') : '';
			return [
				{ label: 'Intestatario', value: d.userDescription },
				{ label: 'P.IVA', value: [d.countryCode, d.vatCode].filter(Boolean).join('') },
				{ label: 'Codice fiscale', value: d.fiscalCode },
				{ label: 'PEC', value: d.pec },
				{ label: 'Scadenza utenza', value: d.expired ? `${scadenza} (scaduta)` : scadenza },
				{ label: 'Spazio usato', value: d.maxSpaceKB ? `${Math.round(d.usedSpaceKB / 1024)} / ${Math.round(d.maxSpaceKB / 1024)} MB` : '' },
			].filter(riga => riga.value);
		},
	},
	methods: {
		codicePagamento(nome) {
			const a = (this.setting.pagamenti || []).find(p => p.name === nome);
			return a ? a.code : '';
		},
		impostaPagamento(nome, code) {
			const altri = (this.setting.pagamenti || []).filter(p => p.name !== nome);
			this.$set(this.setting, 'pagamenti', code ? [...altri, { name: nome, code }] : altri);
		},
	},
};
</script>

<style scoped>
.aruba-detail {
	border: 1px solid rgba(0, 0, 0, 0.12);
	border-radius: 10px;
	overflow: hidden;
	background: #f3f6fa;
	width: 100%;
}
.aruba-body {
	padding: 12px;
}
.aruba-section {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #5f6b7a;
	margin-bottom: 6px;
}
.aruba-grid {
	display: grid;
	grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
	gap: 12px 16px;
}
.aruba-kv {
	display: flex;
	justify-content: space-between;
	gap: 12px;
}
.aruba-kv span:last-child {
	text-align: right;
	word-break: break-word;
}
.aruba-foot {
	display: flex;
	align-items: center;
	flex-wrap: wrap;
	gap: 8px;
	padding: 8px 12px;
	background: #fff;
	border-top: 1px solid #e1e7ef;
}

@media (max-width: 768px) {
	.aruba-body {
		padding: 8px;
	}
	.aruba-grid {
		grid-template-columns: 1fr;
	}
}
</style>
